import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { useMemo, useState } from 'react';
import { t } from 'utils/getTranslations';
import { formatAuditName } from '@common/audit-trail/auditTrailFormat';
import { FilterTriggerIndicator } from './FilterTriggerIndicator';
import { FILTER_TRIGGER_CLASSES } from './toolbarStyles';

export function EntityTypeFilter({
    entityTypes,
    selected,
    onChange,
}: {
    entityTypes: string[];
    selected: string[];
    onChange: (selected: string[]) => void;
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return entityTypes
            .map((entityType) => ({ entityType, label: formatAuditName(entityType) }))
            .filter(({ label }) => !needle || label.toLowerCase().includes(needle));
    }, [entityTypes, query]);
    const allVisibleSelected = visible.every(({ entityType }) => selected.includes(entityType));

    const selectAllVisible = () => {
        onChange([...new Set([...selected, ...visible.map(({ entityType }) => entityType)])]);
    };

    const toggle = (entityType: string) => {
        onChange(
            selected.includes(entityType) ? selected.filter((value) => value !== entityType) : [...selected, entityType]
        );
    };

    return (
        <Popover
            open={open}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (!nextOpen) {
                    setQuery('');
                }
            }}
        >
            <PopoverTrigger className={FILTER_TRIGGER_CLASSES}>
                {t('audit-trail.entity-type')}
                <FilterTriggerIndicator count={selected.length} />
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[280px] rounded-xl p-1.5">
                <Command shouldFilter={false}>
                    <CommandInput value={query} placeholder={t('audit-trail.entity-type')} onValueChange={setQuery} />
                    <CommandList className="max-h-[300px] [scrollbar-gutter:stable]">
                        {visible.length === 0 && <CommandEmpty>{t('audit-trail.no-results')}</CommandEmpty>}
                        <CommandGroup>
                            {visible.map(({ entityType, label }) => (
                                <CommandItem key={entityType} value={entityType} onSelect={() => toggle(entityType)}>
                                    <Checkbox
                                        checked={selected.includes(entityType)}
                                        tabIndex={-1}
                                        className="pointer-events-none"
                                    />
                                    {label}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                    <div className="border-grey-100 flex gap-1 border-t pt-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-grey-600 flex-1 text-[12.5px]"
                            disabled={visible.length === 0 || allVisibleSelected}
                            onClick={selectAllVisible}
                        >
                            {t('audit-trail.select-all')}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-grey-600 flex-1 text-[12.5px]"
                            disabled={selected.length === 0}
                            onClick={() => onChange([])}
                        >
                            {t('audit-trail.clear-all')}
                        </Button>
                    </div>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
